import { ToWords } from "to-words";
import DownloadButton from "../DownloadButton";
import {
  capitalizeFirstLetterOfEachWord,
  convertAmountToWords,
} from "../../utils/Formatter";
import ProfilePlaceholder from "../../assets/svgIcons/ProfilePlaceholder.svg";
import "../../styles/components/transactionPrintLayouts/POInvoiceLayout.css";

export const GoodsReceiptNoteInvoiceLayout = ({
  headDetails,
  companyData,
  itemDetails,
  rowData,
  invoiceTableData,
  selectedReportName,
}) => {
  const toWords = new ToWords();
  const path = window.location.pathname.split("/").filter(Boolean);
  const transactionName = selectedReportName || path[1];

  const organizationData = JSON.parse(localStorage.getItem("organisation"));

  const formatDate = (dateStr) => {
    if (!dateStr) return "--";
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return "0.00";
    return Number(amount).toFixed(2);
  };

  const notes = [
    "Verify that the goods match the purchase order in quantity and specification before accepting.",
    "Inspection report or quality certificate must be checked and filed with the GRN.",
    "Any shortage, damage, or discrepancy must be reported to the purchase department immediately.",
    "Store materials in the designated location after acceptance.",
  ];

  const formatCompanyAddress = (companyData) => {
    if (!companyData) return "N/A";

    const parts = [
      companyData.address?.trim(),
      companyData.city?.trim(),
    ].filter(Boolean);

    const pin = companyData.pincode ? `- ${companyData.pincode}` : "";

    return parts.length > 0 || pin
      ? `${parts.join(", ")} ${pin}`.trim()
      : "N/A";
  };

  const visibleFields = [...rowData]
    .filter((field) => !field.is_internal_field)
    .reverse();

  const calculateTaxFromItems = () => {
    if (!itemDetails || !Array.isArray(itemDetails)) {
      return {
        cgst: 0,
        sgst: 0,
        igst: 0,
        totalGst: 0,
        cgstPercent: 0,
        sgstPercent: 0,
        igstPercent: 0,
      };
    }

    const taxTotals = itemDetails.reduce(
      (totals, item) => {
        const cgstAmt = parseFloat(item.cgst_amt || 0);
        const sgstAmt = parseFloat(item.sgst_amt || 0);
        const igstAmt = parseFloat(item.igst_amt || 0);
        const cgstPer = parseFloat(item.cgst_per || 0);
        const sgstPer = parseFloat(item.sgst_per || 0);
        const igstPer = parseFloat(item.igst_per || 0);

        return {
          cgst: totals.cgst + cgstAmt,
          sgst: totals.sgst + sgstAmt,
          igst: totals.igst + igstAmt,
          cgstPer: totals.cgstPer + cgstPer,
          sgstPer: totals.sgstPer + sgstPer,
          igstPer: totals.igstPer + igstPer,
        };
      },
      { cgst: 0, sgst: 0, igst: 0, cgstPer: 0, sgstPer: 0, igstPer: 0 },
    );

    const totalGst = taxTotals.cgst + taxTotals.sgst + taxTotals.igst;

    const itemCount = itemDetails.length;
    const cgstPercent =
      itemCount > 0 ? (taxTotals.cgstPer / itemCount).toFixed(2) : 0;
    const sgstPercent =
      itemCount > 0 ? (taxTotals.sgstPer / itemCount).toFixed(2) : 0;
    const igstPercent =
      itemCount > 0 ? (taxTotals.igstPer / itemCount).toFixed(2) : 0;

    return {
      cgst: taxTotals.cgst.toFixed(2),
      sgst: taxTotals.sgst.toFixed(2),
      igst: taxTotals.igst.toFixed(2),
      totalGst: totalGst.toFixed(2),
      cgstPercent,
      sgstPercent,
      igstPercent,
    };
  };

  const calculateTotalFromItems = () => {
    if (!itemDetails || !Array.isArray(itemDetails)) {
      return 0;
    }

    const totalAmount = itemDetails.reduce((sum, item) => {
      const amount = parseFloat(item.amount || 0);
      return sum + amount;
    }, 0);

    return totalAmount;
  };

  const { cgst, sgst, igst, totalGst, cgstPercent, sgstPercent, igstPercent } =
    calculateTaxFromItems();
  const totalAmountFromItems = calculateTotalFromItems();
  const finalTotal = totalAmountFromItems + parseFloat(totalGst);
  const amountInWords = convertAmountToWords(finalTotal, toWords);

  return (
    <div className="invoice-container sales-goods-receipt-layout goods-receipt-page-container">
      <h2 className="invoice-title" style={{ textAlign: "center" }}>
        {capitalizeFirstLetterOfEachWord(transactionName || "")}
      </h2>
      <div className="invoice-box">
        <div className="invoice-left">
          <div className="invoice-logo-container">
            <img
              src={organizationData.logo_url || ProfilePlaceholder}
              alt="Logo"
              className="invoice-logo-content"
            />
          </div>
          <div>
            <h3 className="invoice-company-name">
              {organizationData.name || "N/A"}
            </h3>
            <p className="invoice-company-address">
              {formatCompanyAddress(organizationData) || "N/A"}
              <br />
              GSTIN: {organizationData.gst_no || "N/A"}
              <br />
              State: {organizationData.state || "N/A"}
            </p>
          </div>
        </div>
        <div className="invoice-right">
          {[
            { label: "Ref No", value: "FP/GRN/01" },
            { label: "Revision No", value: "01" },
            { label: "Revision Date", value: "06/06/2026" },
          ].map((item, index) => (
            <div key={index} className="purchase-detail">
              <p className="detail-label">
                <strong>{item.label}:</strong>
              </p>
              <div className="detail-value">{item.value}</div>
            </div>
          ))}
        </div>
      </div>

      <div
        className="workorder-header-row workorder-meta-row"
        style={{
          borderLeft: "1px solid #9ca3af",
          borderRight: "1px solid #9ca3af",
          gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
        }}
      >
        <div className="workorder-meta-cell">
          <span className="workorder-meta-label">Goods Receipt Note No:</span>
          <span
            className="workorder-meta-value"
            style={{ whiteSpace: "nowrap" }}
          >
            {headDetails.tran_no}
          </span>
        </div>
        <div className="workorder-meta-cell">
          <span className="workorder-meta-label">Goods Receipt Note Date:</span>
          <span className="workorder-meta-value workorder-date-value">
            {formatDate(headDetails.date || headDetails.created_at)}
          </span>
        </div>
        <div className="workorder-meta-cell">
          <span className="workorder-meta-label">Received Date:</span>
          <span className="workorder-meta-value workorder-date-value">
            {formatDate(headDetails.received_date)}
          </span>
        </div>
      </div>

      <div
        className="workorder-header-row workorder-meta-row"
        style={{
          borderLeft: "1px solid #9ca3af",
          borderRight: "1px solid #9ca3af",
          gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
        }}
      >
        <div className="workorder-meta-cell">
          <span className="workorder-meta-label">PO Number:</span>
          <span className="workorder-meta-value">{headDetails.po_no}</span>
        </div>
        <div className="workorder-meta-cell">
          <span className="workorder-meta-label">PO Date:</span>
          <span className="workorder-meta-value workorder-date-value">
            {formatDate(headDetails.po_date)}
          </span>
        </div>
        <div className="workorder-meta-cell">
          <span className="workorder-meta-label">Work Order No:</span>
          <span className="workorder-meta-value grn-workorder-value">
            {headDetails.wo_no}
          </span>
        </div>
        <div className="workorder-meta-cell">
          <span className="workorder-meta-label">Invoice No:</span>
          <span className="workorder-meta-value">{headDetails.invoice_no}</span>
        </div>
      </div>
      <div className="invoice-supplier-details-container">
        <div className="invoice-supplier-section">
          <h4 className="invoice-section-title">Name of Supplier:</h4>
          <p className="invoice-section-text">
            <span style={{ fontSize: "14px", fontWeight: "bolder" }}>
              {companyData.company_name || "N/A"}
            </span>
            <br />
            Address: {companyData.company_address || "N/A"}
            <br />
            State: Karnataka
          </p>
        </div>
        <div className="invoice-delivery-section">
          <h4 className="invoice-section-title">Delivery at:</h4>
          <p className="invoice-section-text">
            <span style={{ fontSize: "14px", fontWeight: "bolder" }}>
              {organizationData.name || "N/A"}
            </span>
            <br />
            Address:{" "}
            {formatCompanyAddress(organizationData) || "Address not specified"}
            <br />
            GSTIN: {organizationData.gst_no || "N/A"}
            <br />
            State: {organizationData.state || "N/A"}
          </p>
        </div>
      </div>
      <div className="invoice-overflow-x-scroll">
        <table className="invoice-table">
          <thead className="invoice-thead">
            <tr>
              <th className="invoice-th" style={{ borderLeftWidth: "0px" }}>
                No.
              </th>
              {visibleFields.map((field, index) => (
                <th
                  key={index}
                  className="invoice-th"
                  style={
                    index === visibleFields.length - 1
                      ? { borderRightWidth: "0px" }
                      : {}
                  }
                >
                  {field.title || field.field_name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {itemDetails.map((item, index) => (
              <tr key={index}>
                <td className="invoice-td" style={{ borderLeftWidth: "0px" }}>
                  {index + 1}
                </td>
                {[...rowData]
                  .filter((field) => !field.is_internal_field)
                  .reverse()
                  .map((field, idx, arr) => (
                    <td
                      key={idx}
                      className="invoice-td"
                      style={
                        idx === arr.length - 1
                          ? { borderRightWidth: "0px" }
                          : {}
                      }
                    >
                      {item[field.field_name] || "-"}
                    </td>
                  ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="invoice-grid-container">
        <div className="invoice-left-pane">
          <div className="grn-approval-stack">
            <div className="grn-approval-block">
              <div className="workorder-footer-title">Inspected By:</div>
              <div className="workorder-footer-row">
                <span className="workorder-footer-label">Name:</span>
                <span className="workorder-footer-value">
                  {headDetails.inspected_by || headDetails.prepared_by}
                </span>
              </div>
              <div className="workorder-footer-row">
                <span className="workorder-footer-label">Date:</span>
                <span className="workorder-footer-value">
                  {formatDate(headDetails.prepared_date)}
                </span>
              </div>
            </div>
            <div className="grn-approval-block">
              <div className="workorder-footer-title">Approved by:</div>
              <div className="workorder-footer-row">
                <span className="workorder-footer-label">Name:</span>
                <span className="workorder-footer-value">
                  {headDetails.approved_by}
                </span>
              </div>
              <div className="workorder-footer-row">
                <span className="workorder-footer-label">Date:</span>
                <span className="workorder-footer-value">
                  {formatDate(headDetails.approved_at)}
                </span>
              </div>
            </div>
          </div>

          <p className="invoice-section-heading">
            <strong>Goods Receipt Details</strong>
          </p>
          {[
            { label: "Total Quantity", value: headDetails.total_quantity },
            { label: "Net Amount", value: formatCurrency(headDetails.net_amt) },
          ].map((item, index) => (
            <div
              key={index}
              className="invoice-detail-row grn-goods-details-row"
            >
              <p className="invoice-detail-label goods-recipt-label">
                {item.label}:
              </p>
              <div className="invoice-detail-value goods-recipt-value">
                {item.value || "--"}
              </div>
            </div>
          ))}
        </div>
        <div className="invoice-right-pane">
          {[
            {
              label: "Net Amount (Before GST)",
              value: formatCurrency(totalAmountFromItems || 0),
            },
            { label: `CGST @ ${cgstPercent}%`, value: formatCurrency(cgst) },
            { label: `SGST @ ${sgstPercent}%`, value: formatCurrency(sgst) },
            { label: `IGST @ ${igstPercent}%`, value: formatCurrency(igst) },
            { label: "Total GST Amount", value: totalGst },
            { label: "Total", value: formatCurrency(finalTotal) },
          ].map((row, idx, arr) => (
            <div
              className={`invoice-summary-row ${
                idx === arr.length - 1 ? "no-border" : ""
              }`}
              key={idx}
            >
              <p className="invoice-summary-label">
                <strong>{row.label}:</strong>
              </p>
              <div className="invoice-summary-value">{row.value || "-"}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="invoice-grid-container no-top-border">
        <div className="invoice-left-pane note-pane">
          <p
            className="invoice-note-heading"
            style={{
              paddingLeft: "16px",
              paddingRight: "16px",
              paddingTop: "12px",
            }}
          >
            <strong>Note: </strong>
          </p>
          <ul className="invoice-note-list">
            {notes.map((note, index) => (
              <li key={index} className="invoice-note-item">
                {note}
              </li>
            ))}
          </ul>
          <p className="invoice-total-amount">
            <strong>Total Amount: </strong>
            {headDetails?.net_amt ? `${amountInWords} Only` : "N/A"}
          </p>
        </div>

        <div className="invoice-right-pane">
          <div className="invoice-footer-signature">
            <p style={{ margin: "0px", fontSize: "13px" }}>
              <strong>For, {organizationData.name}</strong>
            </p>
          </div>
        </div>
      </div>

      <p className="invoice-system-generated-note">
        This is a system-generated document and does not require a signature.
      </p>

      <div className="invoice-buttons" style={{ paddingTop: "40px" }}>
        <DownloadButton
          tableData={invoiceTableData}
          fileName="invoice"
          downloadFormats={["excel", "pdf", "full-page-pdf"]}
          fullPageDownload={true}
          fullPageSelector=".goods-receipt-page-container"
        />
      </div>
    </div>
  );
};
